import { useRouter } from 'next/router'
import Head from 'next/head'
import { Card, Row, Col, Image, Button } from 'react-bootstrap'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { withIronSessionSsr } from 'iron-session/next'
import { sessionOptions } from '../../../lib/session'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFaceFrown } from '@fortawesome/free-solid-svg-icons'

import JobDetails from '../../../components/JobDetails'


const JobDetailsFullPage = ({ job, googleJobPosting, relatedJobs }) => {
	const { t } = useTranslation()
	const router = useRouter()
	const isEvent = router.query.event !== null || router.query.event !== ''

	const locale = router.locale?.toUpperCase()

	const slugify = (str) => {
		const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;'
		const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------'
		const p = new RegExp(a.split('').join('|'), 'g')

		return str.toString().toLowerCase()
			.replace(/\s+/g, '-') // Replace spaces with -
			.replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
			.replace(/&/g, '-and-') // Replace & with 'and'
			.replace(/[^\w\-]+/g, '') // Remove all non-word characters
			.replace(/\-\-+/g, '-') // Replace multiple - with single -
			.replace(/^-+/, '') // Trim - from start of text
			.replace(/-+$/, '') // Trim - from end of text
	}

	const getSalTyp = (val) => {
		switch (val) {
		case 'PER DAY':
			return t('root.day')
		case 'PER HOUR':
			return t('root.hour')
		case 'PER MONTH':
			return t('root.month')
		case 'PER WEEK':
			return t('root.week')
		default:
			return t('root.month')
		}
	}

	const getJobTypeText = (jt) => {
		return (jt === 'FT' ? t('job_descrip.ft') : (jt === 'PT' ? t('job_descrip.pt') : t('job_descrip.con')))
	}

	return (
		job !== null ? (
			<>
				<Head>
					<title>{`${job.title} - ${job.Company.name}`}</title>
					<meta property='og:url' content={`/${router.locale}/job/${slugify(job.title)}-${job.id}/apply`} />
					<meta property='og:type' content='article' />
					<meta property='og:title' content={job.title} />
					<meta property='og:description' content={`${job.title} at ${job.Company.name}`} />
					<meta property='og:image' content={job.Company.avatar} />
					<meta name='description' content={`${job.title} at ${job.Company.name}`} />
					<script type='application/ld+json' dangerouslySetInnerHTML={{ __html: googleJobPosting }} />
				</Head>
				<Row>
					<Col md={relatedJobs.length > 0 ? 9 : 12}>
						<JobDetails job={job} isFullPage={true} />
					</Col>
					{relatedJobs.length > 0 && !isEvent && <Col>
						<br className='d-block d-sm-none' />
						<h4 className='mb-3'>{t('Jobs_you_might_like')}</h4>
						{relatedJobs.slice(0, 5).map((rj, i) => {
							return <Card key={i} className='mb-3'>
								<Card.Body>
									{rj.Company.avatar && <Image src={rj.Company.avatar} style={{ maxHeight: '70px' }} alt={rj.title} className='mb-2' />}
									<h6>{rj.title}</h6>
									<small>{rj.Company.name}</small>
									<div className='mt-1 mb-3'>
										<small style={{ fontWeight: '500' }}>
											{!rj.hideSalaries && rj.JobSalaries.map((js, i) => {
												return (
													<span key={i}><strong /> <span style={{ display: 'block' }} key={i}><small>{getJobTypeText(js.jobType)}</small> : <span style={{ fontWeight: 600 }}>{js.min > 0 ? `${locale === 'SG' ? '$' : locale === 'MY' ? 'RM' : '$'} ${js.min}` : ''} {js.min > 0 && js.max > 0 ? ' - ' : ''} {js.max > 0 ? `${locale === 'SG' ? '$' : locale === 'MY' ? 'RM' : '$'} ${js.max}` : ''}</span> <small> / {js.salaryType === null ? t('root.month') : getSalTyp(js.salaryType)}</small></span>
													</span>
												)
											})}
										</small>
									</div>
									<Link href={`/${router.locale}/job/${slugify(rj.title)}-${rj.id}${router.query.recommendation ? '?recommendation=' + router.query.recommendation : ''}`}>
										<Button variant='primary' className='text-decoration-none text-white w-100' rel="noreferrer">{t('job.vdetails')}</Button>
									</Link>
								</Card.Body>
							</Card>
						})}
					</Col>}
				</Row>
			</>
		) : (
			<div className='text-center'>
				<FontAwesomeIcon icon={faFaceFrown} className='fa-10x' color='lightgray' />
				<br /><br /><br />
				<h4 style={{ color: 'darkgray' }}>Job not found</h4>

				<p style={{ fontSize: '14px' }}>The Job you&apos;re looking for doesn&apos;t exist</p>
				<Button
					onClick={() => window.location.href = '/browse-jobs'} style={{ color: 'white', fontWeight: 'bold' }}
				>Click here to go back</Button>
			</div>
		)
	)
}

// This gets called on every request
export const getServerSideProps = withIronSessionSsr(
	async function getServerSideProps ({ locale, query, req }) {
		// if its a VCF job, ensure user is logged in
		if (query.event && !req.session.user) {
			return {
				redirect: {
					destination: `/login?redirect=/${locale}/job/${query.id}?event=${query.event}`,
					permanent: false,
				},
			}
		}

		// Fetch data from external API
		let jobId = '0'
		if (typeof query.id !== 'undefined') {
			const queryArray = (query.id as string).split('-')
			jobId = queryArray[queryArray.length - 1]
		}

		// Fetch data from external API
		let apiUrl = `${process.env.NEXT_PUBLIC_API_ENDPOINT}v1/jobseeker/job/${jobId}?`
		if (typeof query.recommendation !== 'undefined') {
			apiUrl += `&recommendation=${query.recommendation}`
		}
		if (typeof query.utm_source !== 'undefined') {
			apiUrl += `&utm_source=${query.utm_source}`
		}
		if (typeof query.utm_medium !== 'undefined') {
			apiUrl += `&utm_medium=${query.utm_medium}`
		}
		if (typeof query.utm_campaign !== 'undefined') {
			apiUrl += `&utm_campaign=${query.utm_campaign}`
		}

		const res = await fetch(apiUrl)
		const Job = await res.json()

		// Get related jobs
		if (Job !== null) {

			let address = '['

			for (let i = 0; i < Job.JobLocations.length; i++) {
				let tempAddress = ''
				if (Job.JobLocations[i].Branch !== null) {
					tempAddress = `{
				"addressCountry": "SG",
				"@type": "PostalAddress",
				"addressLocality": ", Singapore",
				"addressRegion": "SG",
				"postalCode": "${Job.JobLocations[i].Branch.Address.postalCode}",
				"streetAddress": "${Job.JobLocations[i].Branch.Address.streetAddress}"
			}`
				} else {
					tempAddress = `{
				"addressCountry": "SG",
				"@type": "PostalAddress",
				"addressLocality": ", Singapore",
				"addressRegion": "SG",
				"postalCode": "${Job.JobLocations[i].MrtStation?.postalCode}",
				"streetAddress": "${Job.JobLocations[i].MrtStation?.streetAddress}"
			}`
				}
				address = address + tempAddress
				if (i < Job.JobLocations.length - 1) {
					address = address + ','
				}
			}
			address = address + ']'

			let validThrough = Job.jobPostEnd
			if (validThrough === null) {
				const currentDate = new Date(Job.jobPostStart)
				validThrough = new Date(currentDate.setMonth(currentDate.getMonth() + 6)).toISOString()
			}

			const googleJobPosting = `{
				"@context": "https://schema.org/",
				"@type": "JobPosting",
				"title": "${Job.title}",
				"description": "${Job.description !== null ? Job.description.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll('\'', '&#039;') : ''}",
				"identifier": {
					"@type": "PropertyValue",
					"name": "${Job.Company.name}",
					"value": "${Job.Company.id}"
				},
				"datePosted": "${Job.jobPostStart}",
				"validThrough": "${validThrough}",
				"employmentType": ["FULL_TIME", "PART_TIME"],
				"hiringOrganization": {
					"@type": "Organization",
					"name": "${Job.Company.name}",
					"logo": "${Job.Company.avatar}"
				},
				"jobLocation": {
					"@type": "Place",
					"address": ${address}
				},
				"baseSalary": {
					"@type": "MonetaryAmount",
					"currency": "SGD",
					"value": {
						"@type": "QuantitativeValue",
						"value": "${typeof Job.JobSalaries[0] !== 'undefined' ? Job.JobSalaries[0].max : 0}",
						"unitText": "${typeof Job.JobSalaries[0] !== 'undefined' ? Job.JobSalaries[0].salaryType : 'PER MONTH'}"
					}
				}
			}`

			Job.Mrt = Job.JobLocations.filter((jl: any) => {
				return jl.MrtStation !== null
			})

			Job.City = Job.JobLocations.filter((jl: any) => {
				return jl.City !== null
			})

			Job.JobLocations = Job.JobLocations.filter((jl: any) => {
				return jl.Branch !== null
			})

			// Redirect if expired
			if (new Date(Job.jobPostEnd) < new Date()) {
				return {
					notFound: true,
				}
			}

			// Pass data to the page via props
			return {
				props: {
					...(await serverSideTranslations(locale as string, ['common'])),
					job: Job,
					googleJobPosting: googleJobPosting,
					relatedJobs: Job.recommendation,
	    }
			}
		} else {
			return {
				props: {
					...(await serverSideTranslations(locale as string, ['common'])),
					job: Job,
				}
			}
		}
	}, sessionOptions
)

export default JobDetailsFullPage